import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  capturePdfZoomScrollAnchor,
  restorePdfZoomScroll
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';

type UsePdfZoomScrollRestoreParams = {
  wrapperRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  pageRefsMap: MutableRefObject<Map<number, HTMLDivElement>>;
  skipActivePageScrollSyncRef: MutableRefObject<boolean>;
  activePageNumber: number;
  zoomFactor: number;
  pageRefsVersion: number;
  runCanvasLayout: () => void;
  updateActivePageFromScroll: () => void;
};

export function usePdfZoomScrollRestore({
  wrapperRef,
  pageRefsMap,
  skipActivePageScrollSyncRef,
  activePageNumber,
  zoomFactor,
  pageRefsVersion,
  runCanvasLayout,
  updateActivePageFromScroll
}: UsePdfZoomScrollRestoreParams) {
  const zoomRef = useRef(zoomFactor);
  const pendingZoomScrollRef = useRef<ReturnType<
    typeof capturePdfZoomScrollAnchor
  > | null>(null);

  useEffect(() => {
    zoomRef.current = zoomFactor;
  }, [zoomFactor]);

  const captureZoomScrollAnchor = useCallback(
    (nextZoom: number) => {
      if (nextZoom === zoomRef.current) return;
      const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
      if (!viewport) return;

      pendingZoomScrollRef.current = capturePdfZoomScrollAnchor(
        viewport,
        pageRefsMap.current,
        activePageNumber,
        zoomRef.current,
        nextZoom
      );
    },
    [wrapperRef, pageRefsMap, activePageNumber]
  );

  useLayoutEffect(() => {
    const pending = pendingZoomScrollRef.current;
    if (!pending) return;
    pendingZoomScrollRef.current = null;

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return;

    skipActivePageScrollSyncRef.current = true;
    restorePdfZoomScroll(viewport, pageRefsMap.current, pending);

    requestAnimationFrame(() => {
      runCanvasLayout();
      window.setTimeout(() => {
        skipActivePageScrollSyncRef.current = false;
        updateActivePageFromScroll();
      }, 0);
    });
  }, [
    zoomFactor,
    pageRefsVersion,
    wrapperRef,
    pageRefsMap,
    skipActivePageScrollSyncRef,
    runCanvasLayout,
    updateActivePageFromScroll
  ]);

  return { captureZoomScrollAnchor };
}
